// src/routes/AppRoutes.tsx
import React, { lazy, useEffect, useState } from "react";
import { Navigate, RouteObject, useLocation, useNavigate, useRoutes } from "react-router-dom";
import { PATHS } from "./paths";

import Layout from "../components/Layout";
import { clearAuthSession, useAuthSession } from "../lib/auth/session";
import SplashScreen from "../pages/SplashScreen";
import { notifySessionChange } from "../lib/session";

export function ProtectedLayout() {
  const { officerId } = useAuthSession();

  useEffect(() => {
    if (!officerId) {
      clearAuthSession();
      notifySessionChange();
    }
  }, [officerId]);

  if (!officerId) {
    return <Navigate to={PATHS.AUTH_LOGIN} replace />;
  }

  return <Layout />;
}


// Lazy pages (code-splitting)
const Home = lazy(() => import("../pages/Home"));
const Travellers = lazy(() => import("../pages/Travellers")); 
const Inspections = lazy(() => import("../pages/Inspections"));
const QRScanner = lazy(() => import("../pages/QRScanner"));
const Reports = lazy(() => import("../pages/Reports"));
const ReportsConveyanceInspection = lazy(() => import("../pages/reports/conveyance/InspectionReport"));
const ReportsGeneralDeclarations = lazy(() => import("../pages/reports/conveyance/GeneralDeclarationsReport"));
const ReportsCargoSummary = lazy(() => import("../pages/reports/cargo/SummaryReport"));
const ReportsCargoClearance = lazy(() => import("../pages/reports/cargo/ClearanceReport"));
const ReportsCargoHazardous = lazy(() => import("../pages/reports/cargo/HazardousRegisterReport"));
const ReportsCargoCondemnation = lazy(() => import("../pages/reports/cargo/CondemnationReport"));
const ReportsHumanRemainsSummary = lazy(() => import("../pages/reports/human-remains/SummaryReport"));
const ReportsTravellerScreening = lazy(() => import("../pages/reports/traveller/ScreeningReport"));
const ReportsTravellerDiseaseConditions = lazy(() => import("../pages/reports/traveller/DiseaseConditionReport"));
const ReportsEnvironmentalPremises = lazy(() => import("../pages/reports/environmental/PremisesInspectionReport"));
const ReportsEnvironmentalSanitary = lazy(() => import("../pages/reports/environmental/SanitaryScorecardReport"));
const ReportsWaterQuality = lazy(() => import("../pages/reports/water/WaterQualityReport"));
const ReportsNoiseQuality = lazy(() => import("../pages/reports/noise/NoiseQualityReport"));
const ReportsAirQuality = lazy(() => import("../pages/reports/air/AirQualityReport"));
const ReportsVectorSurveillance = lazy(() => import("../pages/reports/vector/SurveillanceSummaryReport"));
const ReportsVectorUsedTyres = lazy(() => import("../pages/reports/vector/UsedTyreReport"));
const ReportsVectorControl = lazy(() => import("../pages/reports/vector/ControlActionsReport"));
const ReportsWasteManagement = lazy(() => import("../pages/reports/waste/WasteManagementReport"));
const ReportsAuditTrail = lazy(() => import("../pages/reports/audit/AuditTrailReport"));
const ReportsCustomBuilder = lazy(() => import("../pages/reports/custom/Builder"));
const ReportsCaptureSummary = lazy(() => import("../pages/reports/capture/CaptureGroupReports"));
const Settings = lazy(() => import("../pages/Settings"));
 
const CallLobby = lazy(() => import("../pages/communications/CallLobby"));
 
const CommunicationsMessagesPage = lazy(() => import("../pages/communications/messages"));
 
const AdminSOPsRegulations = lazy(() => import("../pages/admin/SOPsRegulations"));
const SystemAuditLogs = lazy(() => import("../pages/admin/SystemAuditLogs"));
const HazardousBannedSubstancesList = lazy(() => import("../pages/admin/banned-substances/List"));
const HazardousBannedSubstancesForm = lazy(() => import("../pages/admin/banned-substances/Form"));
const CargoAlertConfigurationPage = lazy(() => import("../pages/admin/cargo-alerts/Configuration"));
// const Help = lazy(() => import("../pages/Help"));
const NotFound = lazy(() => import("../pages/NotFound"));
const UserManagementLanding = lazy(() => import("../pages/user-management/UserManagementLanding"));
const RoleManagement = lazy(() => import("../pages/user-management/RoleManagement"));
const CreatePoE = lazy(() => import("../pages/user-management/CreatePoE"));
const UserProfile = lazy(() => import("../pages/user-management/UserProfile"));
const UserManagementLogin = lazy(() => import("../pages/user-management/Login"));

//Human Remain
const HumanRemains = lazy(() => import("../pages/human-remains/clearance-form/ClearanceForm"));
const HumanRemainsList = lazy(() => import("../pages/human-remains/clearance-form/List"));

const AircraftList = lazy(() => import("../pages/aircraft/List"));
const AircraftListAdd = lazy(() => import("../pages/aircraft/Checklist"));
const AircraftChecklistView = lazy(() =>
  import("../pages/aircraft/Checklist").then((m) => ({ default: m.AircraftChecklistView })),
);
const AircraftChecklistEdit = lazy(() =>
  import("../pages/aircraft/Checklist").then((m) => ({ default: m.AircraftChecklistEdit })),
);

const GeneralDeclarationList = lazy(() => import("../pages/general-declarations/List"));
const GeneralDeclarationCreate = lazy(() => import("../pages/general-declarations/Create"));

const VehicleGeneralDeclarationsList = lazy(
  () => import("../pages/general-declarations-vehicles/List"),
);
const VehicleGeneralDeclarationForm = lazy(
  () => import("../pages/general-declarations-vehicles/Create"),
);

const VehicleHazardChecklistList = lazy(
  () => import("../pages/conveyance/vehicle-hazard-checklists/List"),
);
const VehicleHazardChecklistCreate = lazy(
  () => import("../pages/conveyance/vehicle-hazard-checklists/Form"),
);
const VehicleHazardChecklistView = lazy(() =>
  import("../pages/conveyance/vehicle-hazard-checklists/Form").then((m) => ({
    default: m.VehicleHazardChecklistView,
  })),
);
const VehicleHazardChecklistEdit = lazy(() =>
  import("../pages/conveyance/vehicle-hazard-checklists/Form").then((m) => ({
    default: m.VehicleHazardChecklistEdit,
  })),
);

const ConveyanceChecklistList = lazy(() => import("../pages/conveyance/checklists/List"));

const PremiseList = lazy(() => import("../pages/premises/inspection/List"));
const PremiseCreate = lazy(() => import("../pages/premises/inspection/Create"));

const CargoList = lazy(() => import("../pages/cargo/inspections/List"));
const CargoCreate = lazy(() => import("../pages/cargo/inspections/Create"));
const CargoWarehouseInspectionsList = lazy(
  () => import("../pages/cargo/warehouse-inspections/List"),
);
const CargoWarehouseInspectionForm = lazy(
  () => import("../pages/cargo/warehouse-inspections/Form"),
);

const VectorSurveillanceList = lazy(() => import("../pages/vector/surveillance/List"));
const VectorSurveillanceCreate = lazy(() => import("../pages/vector/surveillance/Form"));
const VectorSurveillanceEdit = lazy(() =>
  import("../pages/vector/surveillance/Form").then((m) => ({ default: m.VectorSurveillanceEdit })),
);
const VectorSurveillanceView = lazy(() =>
  import("../pages/vector/surveillance/Form").then((m) => ({ default: m.VectorSurveillanceView })),
);
const VectorTyreInspectionList = lazy(() => import("../pages/vector/tyre/List"));
const VectorTyreInspectionForm = lazy(() => import("../pages/vector/tyre/Form"));
const RodentInspectionList = lazy(() => import("../pages/vector/rodent/List"));

const FoodWaterInspectionList = lazy(() => import("../pages/food-water/List"));
const FoodWaterInspectionCreate = lazy(() => import("../pages/food-water/Create"));

const SanitaryInspectionCreate = lazy(() => import("../pages/sanitary/Create"));
 const SanitaryInspectionList = lazy(() => import("../pages/sanitary/List"));

const WasteDisposalCreate = lazy(() => import("../pages/waste-disposal/Form"));
 const WasteDisposalList = lazy(() => import("../pages/waste-disposal/List"));

 const PremiseLicenceCreate = lazy(() => import("../pages/licences/premise/PremiseCreate"));
 const PremiseLicenceList = lazy(() => import("../pages/licences/premise/PremiseList"));

const FoodHandlerCreate = lazy(() => import("../pages/licences/FoodHandlersCreate"));
const FoodHandlerList = lazy(() => import("../pages/licences/FoodHandlersList"));

const Watchlist = lazy(() => import("../pages/licences/WatchList"));

const CountryDocumentationList = lazy(() => import("../pages/admin/country-documentation/List"));
const CountryDocumentationForm = lazy(() => import("../pages/admin/country-documentation/Form"));
const DocumentRequirementsList = lazy(() => import("../pages/admin/document-requirements/List"));
const DocumentRequirementForm = lazy(() => import("../pages/admin/document-requirements/Form"));

const TravellerDeclarationCreate = lazy(() => import("../pages/traveller/thdf/TravellerDeclarationCreate"));
const TravellerDeclarationView = lazy(() => import("../pages/traveller/thdf/TravellerDeclarationList"));
 
 
const ClearanceCertificates = lazy(() => import("../pages/traveller/clearance-certificates/List"));
const ClearanceCertificatesCreate = lazy(() => import("../pages/traveller/clearance-certificates/Form"));
const HumanRemainsCertificateView = lazy(
  () => import("../pages/human-remains/clearance-certificates/View"),
);

const ScreeningList = lazy(() => import("../pages/traveller/screening/List"));
const ScreeningForm = lazy(() => import("../pages/traveller/screening/Form"));
const IllPassengerReportsList = lazy(() => import("../pages/traveller/ill-passenger-reports/List"));
const IllPassengerReportsCreate = lazy(() => import("../pages/traveller/ill-passenger-reports/Form"));
const IllPassengerReportsEdit = lazy(() =>
  import("../pages/traveller/ill-passenger-reports/Form").then((m) => ({ default: m.IllPassengerReportsEdit })),
);
const IllPassengerReportsDetail = lazy(() => import("../pages/traveller/ill-passenger-reports/Detail"));
const TravellerScreeningDetail = lazy(() => import("../pages/traveller/screening/Detail"));
const PrimaryScreeningList = lazy(() => import("../pages/traveller/primary-screening/List"));
const PrimaryScreeningForm = lazy(() => import("../pages/traveller/primary-screening/Form"));
const DocumentVerificationList = lazy(() => import("../pages/traveller/document-verification/List"));
const DocumentVerificationCreate = lazy(() => import("../pages/traveller/document-verification/Form"));
const HoldingBayLogsList = lazy(() => import("../pages/conveyance/holding-bay-logs/List"));
const HoldingBayLogsCreate = lazy(() => import("../pages/conveyance/holding-bay-logs/Form"));

// New: Surveillance Cases
const SurveillanceCasesList = lazy(() => import("../pages/surveillance/cases/List"));
const SurveillanceCasesCreate = lazy(() => import("../pages/surveillance/cases/Form"));
const SurveillanceCaseDetail = lazy(() => import("../pages/surveillance/cases/Detail"));

// New: Aircraft Clearance (Disinfection + Free Pratique)
const AircraftClearanceList = lazy(() => import("../pages/conveyance/clearance/List"));
const AircraftClearanceCreate = lazy(() => import("../pages/conveyance/clearance/Form"));

// New: Detention Certificates
const DetentionCertificatesList = lazy(() => import("../pages/vector/detention-certificates/List"));
const DetentionCertificatesCreate = lazy(() => import("../pages/vector/detention-certificates/Form"));
const DetentionCertificatesDetail = lazy(() => import("../pages/vector/detention-certificates/Detail"));

// New: Ovitrap Monitoring
const OvitrapsList = lazy(() => import("../pages/vector/ovitraps/List"));
const OvitrapsCreate = lazy(() => import("../pages/vector/ovitraps/Form"));

// New: Residual Spraying
const ResidualSprayList = lazy(() => import("../pages/vector/residual-spraying/List"));
const ResidualSprayCreate = lazy(() => import("../pages/vector/residual-spraying/Form"));
const ResidualSprayEdit = lazy(() =>
  import("../pages/vector/residual-spraying/Form").then((m) => ({ default: m.ResidualSprayEdit })),
);
const ResidualSprayView = lazy(() =>
  import("../pages/vector/residual-spraying/Form").then((m) => ({ default: m.ResidualSprayView })),
);

// New: Yellow Fever Checks
const YellowFeverList = lazy(() => import("../pages/traveller/yellow-fever/List"));
const YellowFeverCreate = lazy(() => import("../pages/traveller/yellow-fever/Form"));

// New: Vector Reports (Used Tyres Monthly)
const UsedTyresMonthly = lazy(() => import("../pages/vector/reports/tyres/Monthly"));

const BusTruckInspectionsList = lazy(() => import("../pages/conveyance/bus-truck-inspections/List"));
const BusTruckInspectionsCreate = lazy(() => import("../pages/conveyance/bus-truck-inspections/Form"));
const PassengerTrainInspectionsList = lazy(
  () => import("../pages/conveyance/passenger-train-inspections/List"),
);
const PassengerTrainInspectionCreate = lazy(
  () => import("../pages/conveyance/passenger-train-inspections/Form"),
);
const PassengerTrainInspectionView = lazy(() =>
  import("../pages/conveyance/passenger-train-inspections/Form").then((m) => ({
    default: m.PassengerTrainInspectionView,
  })),
);
const PassengerTrainInspectionEdit = lazy(() =>
  import("../pages/conveyance/passenger-train-inspections/Form").then((m) => ({
    default: m.PassengerTrainInspectionEdit,
  })),
);
const BusChecklistsList = lazy(() => import("../pages/conveyance/bus-checklists/List"));
const BusChecklistCreate = lazy(() => import("../pages/conveyance/bus-checklists/Checklist"));
const BusChecklistView = lazy(() =>
  import("../pages/conveyance/bus-checklists/Checklist").then((m) => ({ default: m.BusChecklistView })),
);
const BusChecklistEdit = lazy(() =>
  import("../pages/conveyance/bus-checklists/Checklist").then((m) => ({ default: m.BusChecklistEdit })),
);
const OpenCasesList = lazy(() => import("../pages/conveyance/cases/open/List"));
const OpenCasesCreate = lazy(() => import("../pages/conveyance/cases/open/Form"));

const PendingReviewList = lazy(() => import("../pages/conveyance/cases/pending/List"));
const PendingReviewForm = lazy(() => import("../pages/conveyance/cases/pending/Form"));
const EscalatedCasesList = lazy(() => import("../pages/conveyance/cases/escalated/List"));
const EscalatedCasesCreate = lazy(() => import("../pages/conveyance/cases/escalated/Form"));
 
const SanitationCertsList = lazy(() => import("../pages/conveyance/sanitation-certificates/List"));
const SanitationCertsCreate = lazy(() => import("../pages/conveyance/sanitation-certificates/Form"));


const CargoInspectionOutcomesList = lazy(() => import("../pages/cargo/inspection-outcomes/List"));
const CargoInspectionOutcomesCreate = lazy(() => import("../pages/cargo/inspection-outcomes/Form"));
const HazardousSubstancesList = lazy(() => import("../pages/cargo/hazardous-substances/List"));
const HazardousSubstancesCreate = lazy(() => import("../pages/cargo/hazardous-substances/Form"));
const CargoTireInspectionsList = lazy(() => import("../pages/cargo/tire-inspections/List"));
const CargoTireInspectionCreate = lazy(() => import("../pages/cargo/tire-inspections/Form"));
const CargoTireInspectionView = lazy(() =>
  import("../pages/cargo/tire-inspections/Form").then((m) => ({ default: m.TireInspectionView })),
);
const CargoTireInspectionEdit = lazy(() =>
  import("../pages/cargo/tire-inspections/Form").then((m) => ({ default: m.TireInspectionEdit })),
);
const CargoCertificatesList = lazy(() => import("../pages/cargo/certificates/List"));
  const CargoCertificatesCreate = lazy(() => import("../pages/cargo/certificates/Form"));
const HoldingBayInspectionsList = lazy(() => import("../pages/cargo/holding-bay-inspections/List"));
const HoldingBayInspectionsCreate = lazy(() => import("../pages/cargo/holding-bay-inspections/Form"));
const WaterQualityList = lazy(() => import("../pages/premises/water-quality/List"));
  const WaterQualityCreate = lazy(() => import("../pages/premises/water-quality/Form"));
const NoiseMonitoringList = lazy(() => import("../pages/environmental-health/noise-monitoring/List"));
const NoiseMonitoringCreate = lazy(() => import("../pages/environmental-health/noise-monitoring/Form"));
const HealthEducationList = lazy(() => import("../pages/environmental-health/health-education/List"));
const HealthEducationCreate = lazy(() => import("../pages/environmental-health/health-education/Form"));
const TrainingMeetingsList = lazy(
  () => import("../pages/environmental-health/trainings-meetings/List"),
);
const TrainingMeetingsForm = lazy(
  () => import("../pages/environmental-health/trainings-meetings/Form"),
);
const AirQualityList = lazy(() => import("../pages/environmental-health/indoor-air-quality/List"));
  const AirQualityCreate = lazy(() => import("../pages/environmental-health/indoor-air-quality/Form"));

const DoACasesList = lazy(() => import("../pages/human-remains/doa-cases/List"));
const DoACasesForm = lazy(() => import("../pages/human-remains/doa-cases/Form"));
const WaterSamplingKitsList = lazy(() => import("../pages/equipment/water-sampling-kits/List"));
const WaterSamplingKitsCreate = lazy(() => import("../pages/equipment/water-sampling-kits/Form"));
const FoodSamplingKitsList = lazy(() => import("../pages/equipment/food-sampling-kits/List"));
const FoodSamplingKitsCreate = lazy(() => import("../pages/equipment/food-sampling-kits/Form"));
const RapidDiagnosticKitsList = lazy(() => import("../pages/equipment/rapid-diagnostic-kits/List"));
const RapidDiagnosticKitsCreate = lazy(() => import("../pages/equipment/rapid-diagnostic-kits/Form"));
const RadiationMonitorsList = lazy(() => import("../pages/equipment/radiation-monitors/List"));
const RadiationMonitorsCreate = lazy(() => import("../pages/equipment/radiation-monitors/Form"));
const MonitoringBadgesList = lazy(() => import("../pages/equipment/monitoring-badges/List"));
const MonitoringBadgesCreate = lazy(() => import("../pages/equipment/monitoring-badges/Form"));
  const TyreInspectionList = lazy(() => import("../pages/vector/tyre/List"));
  const TyreInspectionCreate = lazy(() => import("../pages/vector/tyre/Form"));
const DeratingCertificatesList = lazy(() => import("../pages/surveillance/derating-certificates/List"));
const DeratingCertificatesCreate = lazy(() => import("../pages/surveillance/derating-certificates/Form"));
const OutbreakAlertsList = lazy(() => import("../pages/outbreak/alerts/List"));
const OutbreakAlertsCreate = lazy(() => import("../pages/outbreak/alerts/Form"));
const OutbreakAlertsEdit = lazy(() =>
  import("../pages/outbreak/alerts/Form").then((m) => ({ default: m.OutbreakAlertsEdit })),
);
const OutbreakAlertsView = lazy(() =>
  import("../pages/outbreak/alerts/Form").then((m) => ({ default: m.OutbreakAlertsView })),
);
  const OutbreakContactTracingList = lazy(() => import("../pages/outbreak/contact-tracing/List"));
  const OutbreakContactTracingCreate = lazy(() => import("../pages/outbreak/contact-tracing/CreateCase"));
  const OutbreakContactTracingWorkspace = lazy(() => import("../pages/outbreak/contact-tracing/Form"));
const OutbreakDrillsList = lazy(() => import("../pages/outbreak/drills/List"));
const OutbreakDrillsCreate = lazy(() => import("../pages/outbreak/drills/Form"));
const OutbreakDrillsEdit = lazy(() =>
  import("../pages/outbreak/drills/Form").then((m) => ({ default: m.OutbreakDrillsEdit })),
);
const OutbreakDrillsView = lazy(() =>
  import("../pages/outbreak/drills/Form").then((m) => ({ default: m.OutbreakDrillsView })),
);
const OutbreakLogbookList = lazy(() => import("../pages/outbreak/logbook/List"));
const OutbreakLogbookCreate = lazy(() => import("../pages/outbreak/logbook/Form"));
const OutbreakLogbookEdit = lazy(() =>
  import("../pages/outbreak/logbook/Form").then((m) => ({ default: m.OutbreakLogbookEdit })),
);
const OutbreakLogbookView = lazy(() =>
  import("../pages/outbreak/logbook/Form").then((m) => ({ default: m.OutbreakLogbookView })),
);
const OutbreakRumourRegisterList = lazy(() => import("../pages/outbreak/rumour-register/List"));
const OutbreakRumourRegisterCreate = lazy(() => import("../pages/outbreak/rumour-register/Form"));
const OutbreakRumourRegisterEdit = lazy(() =>
  import("../pages/outbreak/rumour-register/Form").then((m) => ({ default: m.OutbreakRumourRegisterEdit })),
);
const OutbreakRumourRegisterView = lazy(() =>
  import("../pages/outbreak/rumour-register/Form").then((m) => ({ default: m.OutbreakRumourRegisterView })),
);
const EmergencyPlansList = lazy(() => import("../pages/outbreak/emergency/List"));
const EmergencyPlansCreate = lazy(() => import("../pages/outbreak/emergency/Form"));
const EmergencyPlansEdit = lazy(() =>
  import("../pages/outbreak/emergency/Form").then((m) => ({ default: m.EmergencyPlansEdit })),
);
const EmergencyPlansView = lazy(() =>
  import("../pages/outbreak/emergency/Form").then((m) => ({ default: m.EmergencyPlansView })),
);
const ReportsCenterList = lazy(() => import("../pages/reports/center/List"));
const ReportsCenterCreate = lazy(() => import("../pages/reports/center/Form"));
const ReportsExportsGeneralDeclaration = lazy(() => import("../pages/reports/exports/GeneralDeclarationRegister"));
const ReportsExportsSurveillance = lazy(() => import("../pages/reports/exports/SurveillanceRegister"));
const ReportsExportsChainOfCustody = lazy(() => import("../pages/reports/exports/SamplingChainOfCustody"));
const ReportsExportsLicenceExpiry = lazy(() => import("../pages/reports/exports/LicenceExpiryRegister"));
const ReportsCargoSmuggledGoods = lazy(() => import("../pages/reports/cargo/SmuggledGoodsReport"));
const ReportsDailyAudit = lazy(() => import("../pages/reports/DailyAuditReport"));

const PortOpsDashboard = lazy(() => import("../pages/reports/ops/PortOpsDashboard"));
const VectorDashboard = lazy(() => import("../pages/reports/ops/VectorDashboard"));

// Dashboards (domain hubs)
const DashboardPortOperations = lazy(() => import("../pages/dashboards/port-operations"));
const DashboardTravellerHealth = lazy(() => import("../pages/dashboards/traveller-health"));
const DashboardConveyanceOversight = lazy(() => import("../pages/dashboards/conveyance-oversight"));
const DashboardCargoHoldingBay = lazy(() => import("../pages/dashboards/cargo-holding-bay"));
const DashboardHumanRemains = lazy(() => import("../pages/dashboards/human-remains"));
const DashboardVectorSurveillance = lazy(() => import("../pages/dashboards/vector-surveillance"));
const DashboardEnvironmentalHealth = lazy(() => import("../pages/dashboards/environmental-health"));
const DashboardOutbreaksEpr = lazy(() => import("../pages/dashboards/outbreaks-epr"));

const DashboardFinanceRevenue = lazy(() => import("../pages/dashboards/finance/Revenue"));
const DashboardFinanceZimra = lazy(() => import("../pages/dashboards/finance/Zimra"));
const DashboardFinanceRecon = lazy(() => import("../pages/dashboards/finance/Reconciliation"));

const DashboardAdmin = lazy(() => import("../pages/dashboards/admin"));
const DashboardAdminMakerChecker = lazy(() => import("../pages/dashboards/admin/MakerChecker"));
const DashboardAdminCompliance = lazy(() => import("../pages/dashboards/admin/Compliance"));

const DashboardOpsPortDaily = lazy(() => import("../pages/dashboards/ops/PortDaily"));
const DashboardOpsCompliance = lazy(() => import("../pages/dashboards/ops/Compliance"));
const DashboardOpsRisk = lazy(() => import("../pages/dashboards/ops/RiskAlerts"));

const withSuspense = (el: React.ReactNode) => (
  <React.Suspense fallback={<div className="p-6">Loading…</div>}>{el}</React.Suspense>
);

const ProtectedNotFound = () => {
  const { officerId } = useAuthSession();

  if (!officerId) {
    return <Navigate to={PATHS.AUTH_LOGIN} replace />;
  }

  return withSuspense(<NotFound />);
};

// Route objects (React Router v6+)
const routes: RouteObject[] = [
  { path: PATHS.AUTH_LOGIN, element: withSuspense(<UserManagementLogin />) },
  {
    path: PATHS.ROOT,
    element: <ProtectedLayout />,
    children: [
      { index: true, element: withSuspense(<Home />) },
      { path: PATHS.TRAVELLERS, element: withSuspense(<Travellers />) },
      
     
     { path: PATHS.TRAVELLERS_DOC_VERIFY_LIST, element: withSuspense(<DocumentVerificationList />) },
     { path: PATHS.TRAVELLERS_DOC_VERIFY_CREATE, element: withSuspense(<DocumentVerificationCreate />) },
 
{ path: PATHS.TRAVELLERS_CLEARANCE_CERTS, element: withSuspense(<ClearanceCertificates />) },
{ path: PATHS.TRAVELLERS_CLEARANCE_CERTS_CREATE, element: withSuspense(<ClearanceCertificatesCreate />) },
      { path: PATHS.TRAVELLERS_SCREENING_LIST, element: withSuspense(<ScreeningList />) },
      { path: PATHS.TRAVELLERS_SCREENING_CREATE, element: withSuspense(<ScreeningForm />) },
      { path: PATHS.TRAVELLERS_SCREENING_EDIT, element: withSuspense(<ScreeningForm />) },
      { path: PATHS.TRAVELLERS_SCREENING_VIEW, element: withSuspense(<TravellerScreeningDetail />) },
      { path: PATHS.TRAVELLER_PRIMARY_SCREENING_LIST, element: withSuspense(<PrimaryScreeningList />) },
      { path: PATHS.TRAVELLER_PRIMARY_SCREENING_CREATE, element: withSuspense(<PrimaryScreeningForm />) },
      { path: PATHS.TRAVELLER_PRIMARY_SCREENING_EDIT, element: withSuspense(<PrimaryScreeningForm />) },
      { path: PATHS.TRAVELLER_PRIMARY_SCREENING_VIEW, element: withSuspense(<PrimaryScreeningForm />) },
{ path: PATHS.PASSENGER_ILL_REPORTS_LIST, element: withSuspense(<IllPassengerReportsList />) },
{ path: PATHS.PASSENGER_ILL_REPORTS_CREATE, element: withSuspense(<IllPassengerReportsCreate />) },
{ path: PATHS.PASSENGER_ILL_REPORTS_EDIT, element: withSuspense(<IllPassengerReportsEdit />) },
{ path: PATHS.PASSENGER_ILL_REPORTS_VIEW, element: withSuspense(<IllPassengerReportsDetail />) },

      { path: PATHS.CONVEYANCE_ROAD_INSPECTIONS_LIST, element: withSuspense(<BusTruckInspectionsList />) },
      { path: PATHS.CONVEYANCE_ROAD_INSPECTIONS_CREATE, element: withSuspense(<BusTruckInspectionsCreate />) },
      {
        path: PATHS.PASSENGER_TRAIN_INSPECTIONS_LIST,
        element: withSuspense(<PassengerTrainInspectionsList />),
      },
      {
        path: PATHS.PASSENGER_TRAIN_INSPECTIONS_CREATE,
        element: withSuspense(<PassengerTrainInspectionCreate />),
      },
      {
        path: PATHS.PASSENGER_TRAIN_INSPECTIONS_VIEW,
        element: withSuspense(<PassengerTrainInspectionView />),
      },
      {
        path: PATHS.PASSENGER_TRAIN_INSPECTIONS_EDIT,
        element: withSuspense(<PassengerTrainInspectionEdit />),
      },
      { path: PATHS.CONVEYANCE_BUS_CHECKLISTS_LIST, element: withSuspense(<BusChecklistsList />) },
      { path: PATHS.CONVEYANCE_BUS_CHECKLISTS_CREATE, element: withSuspense(<BusChecklistCreate />) },
      { path: PATHS.CONVEYANCE_BUS_CHECKLISTS_VIEW, element: withSuspense(<BusChecklistView />) },
      { path: PATHS.CONVEYANCE_BUS_CHECKLISTS_EDIT, element: withSuspense(<BusChecklistEdit />) },
      { path: PATHS.CONVEYANCE_CHECKLISTS, element: withSuspense(<ConveyanceChecklistList />) },
      { path: PATHS.CONVEYANCE_HOLDING_BAY_LOGS, element: withSuspense(<HoldingBayLogsList />) },
      { path: PATHS.CONVEYANCE_HOLDING_BAY_LOGS_CREATE, element: withSuspense(<HoldingBayLogsCreate />) },

      // Surveillance Cases
      { path: PATHS.SURVEILLANCE_CASES_LIST, element: withSuspense(<SurveillanceCasesList />) },
      { path: PATHS.SURVEILLANCE_CASES_CREATE, element: withSuspense(<SurveillanceCasesCreate />) },
      { path: PATHS.SURVEILLANCE_CASES_VIEW, element: withSuspense(<SurveillanceCaseDetail />) },

      // Aircraft Clearance
      { path: PATHS.CONVEYANCE_CLEARANCE_LIST, element: withSuspense(<AircraftClearanceList />) },
      { path: PATHS.CONVEYANCE_CLEARANCE_CREATE, element: withSuspense(<AircraftClearanceCreate />) },

{ path: PATHS.CASES_OPEN_LIST, element: withSuspense(<OpenCasesList />) },
{ path: PATHS.CASES_OPEN_CREATE, element: withSuspense(<OpenCasesCreate />) },
{ path: PATHS.CASES_PENDING, element: withSuspense(<PendingReviewList />) },
{ path: PATHS.CASES_PENDING_CREATE, element: withSuspense(<PendingReviewForm />) },
{ path: PATHS.CASES_ESCALATED_LIST, element: withSuspense(<EscalatedCasesList />) },
{ path: PATHS.CASES_ESCALATED_CREATE, element: withSuspense(<EscalatedCasesCreate />) },
 
{ path: PATHS.SANITATION_CERTS, element: withSuspense(<SanitationCertsList />) },
{ path: PATHS.SANITATION_CERTS_CREATE, element: withSuspense(<SanitationCertsCreate />) },

{ path: PATHS.CARGO_INSPECTION_OUTCOMES_LIST, element: withSuspense(<CargoInspectionOutcomesList />) },
{ path: PATHS.CARGO_INSPECTION_OUTCOMES_CREATE, element: withSuspense(<CargoInspectionOutcomesCreate />) },
{ path: PATHS.CARGO_HAZARDOUS_SUBSTANCES_LIST, element: withSuspense(<HazardousSubstancesList />) },
{ path: PATHS.CARGO_TIRE_INSPECTIONS_LIST, element: withSuspense(<CargoTireInspectionsList />) },
{ path: PATHS.CARGO_TIRE_INSPECTIONS_CREATE, element: withSuspense(<CargoTireInspectionCreate />) },
{ path: PATHS.CARGO_TIRE_INSPECTIONS_VIEW, element: withSuspense(<CargoTireInspectionView />) },
{ path: PATHS.CARGO_TIRE_INSPECTIONS_EDIT, element: withSuspense(<CargoTireInspectionEdit />) },
{ path: PATHS.CARGO_HAZARDOUS_SUBSTANCES_CREATE, element: withSuspense(<HazardousSubstancesCreate />) },
{ path: PATHS.CARGO_CERTIFICATES_LIST, element: withSuspense(<CargoCertificatesList />) },
{ path: PATHS.CARGO_CERTIFICATES_CREATE, element: withSuspense(<CargoCertificatesCreate />) },
{ path: PATHS.CARGO_WAREHOUSE_INSPECTIONS_LIST, element: withSuspense(<CargoWarehouseInspectionsList />) },
{ path: PATHS.CARGO_WAREHOUSE_INSPECTIONS_CREATE, element: withSuspense(<CargoWarehouseInspectionForm />) },
{ path: PATHS.CARGO_HOLDING_BAY_INSPECTIONS_LIST, element: withSuspense(<HoldingBayInspectionsList />) },
{ path: PATHS.CARGO_HOLDING_BAY_INSPECTIONS_CREATE, element: withSuspense(<HoldingBayInspectionsCreate />) },

{ path: PATHS.PREMISES_WATER_QUALITY_LIST, element: withSuspense(<WaterQualityList />) },
  { path: PATHS.PREMISES_WATER_QUALITY_CREATE, element: withSuspense(<WaterQualityCreate />) },

  { path: PATHS.EH_INDOOR_AIR_QUALITY_LIST, element: withSuspense(<AirQualityList />) },
  { path: PATHS.EH_INDOOR_AIR_QUALITY_CREATE, element: withSuspense(<AirQualityCreate />) },

  { path: PATHS.EH_NOISE_MONITORING_LIST, element: withSuspense(<NoiseMonitoringList />) },
  { path: PATHS.EH_NOISE_MONITORING_CREATE, element: withSuspense(<NoiseMonitoringCreate />) },
  { path: PATHS.EH_HEALTH_EDUCATION_LIST, element: withSuspense(<HealthEducationList />) },
  { path: PATHS.EH_HEALTH_EDUCATION_CREATE, element: withSuspense(<HealthEducationCreate />) },
  { path: PATHS.EH_TRAINING_MEETINGS_LIST, element: withSuspense(<TrainingMeetingsList />) },
  { path: PATHS.EH_TRAINING_MEETINGS_CREATE, element: withSuspense(<TrainingMeetingsForm />) },

{ path: PATHS.HR_DOA_CASES_LIST, element: withSuspense(<DoACasesList />) },
{ path: PATHS.HR_DOA_CASES_CREATE, element: withSuspense(<DoACasesForm />) },
{ path: PATHS.EQUIPMENT_WATER_SAMPLING_LIST, element: withSuspense(<WaterSamplingKitsList />) },
{ path: PATHS.EQUIPMENT_WATER_SAMPLING_CREATE, element: withSuspense(<WaterSamplingKitsCreate />) },
{ path: PATHS.EQUIPMENT_FOOD_SAMPLING_LIST, element: withSuspense(<FoodSamplingKitsList />) },
{ path: PATHS.EQUIPMENT_FOOD_SAMPLING_CREATE, element: withSuspense(<FoodSamplingKitsCreate />) },
{ path: PATHS.EQUIPMENT_RAPID_DIAGNOSTIC_LIST, element: withSuspense(<RapidDiagnosticKitsList />) },
{ path: PATHS.EQUIPMENT_RAPID_DIAGNOSTIC_CREATE, element: withSuspense(<RapidDiagnosticKitsCreate />) },
{ path: PATHS.EQUIPMENT_RADIATION_MONITORS_LIST, element: withSuspense(<RadiationMonitorsList />) },
{ path: PATHS.EQUIPMENT_RADIATION_MONITORS_CREATE, element: withSuspense(<RadiationMonitorsCreate />) },
{ path: PATHS.EQUIPMENT_MONITORING_BADGES_LIST, element: withSuspense(<MonitoringBadgesList />) },
{ path: PATHS.EQUIPMENT_MONITORING_BADGES_CREATE, element: withSuspense(<MonitoringBadgesCreate />) },
  { path: PATHS.VECTOR_TYRE_INSPECTIONS_LIST, element: withSuspense(<TyreInspectionList />) },
  { path: PATHS.VECTOR_TYRE_INSPECTIONS_CREATE, element: withSuspense(<TyreInspectionCreate />) },

{ path: PATHS.VECTOR_DERATING_CERTIFICATES_LIST, element: withSuspense(<DeratingCertificatesList />) },
{ path: PATHS.VECTOR_DERATING_CERTIFICATES_CREATE, element: withSuspense(<DeratingCertificatesCreate />) },

  // Detention Certificates
  { path: PATHS.VECTOR_DETENTION_CERTS_LIST, element: withSuspense(<DetentionCertificatesList />) },
  { path: PATHS.VECTOR_DETENTION_CERTS_CREATE, element: withSuspense(<DetentionCertificatesCreate />) },
  { path: PATHS.VECTOR_DETENTION_CERTS_VIEW, element: withSuspense(<DetentionCertificatesDetail />) },

  { path: PATHS.OUTBREAK_ALERTS, element: withSuspense(<OutbreakAlertsList />) },
  { path: PATHS.OUTBREAK_ALERTS_CREATE, element: withSuspense(<OutbreakAlertsCreate />) },
  { path: PATHS.OUTBREAK_ALERTS_VIEW, element: withSuspense(<OutbreakAlertsView />) },
  { path: PATHS.OUTBREAK_ALERTS_EDIT, element: withSuspense(<OutbreakAlertsEdit />) },
  { path: PATHS.OUTBREAK_CONTACT_TRACING, element: withSuspense(<OutbreakContactTracingList />) },
  { path: PATHS.OUTBREAK_CONTACT_TRACING_CREATE, element: withSuspense(<OutbreakContactTracingCreate />) },
  { path: PATHS.OUTBREAK_CONTACT_TRACING_CASE, element: withSuspense(<OutbreakContactTracingWorkspace />) },
  { path: PATHS.OUTBREAK_DRILLS, element: withSuspense(<OutbreakDrillsList />) },
  { path: PATHS.OUTBREAK_DRILLS_CREATE, element: withSuspense(<OutbreakDrillsCreate />) },
  { path: PATHS.OUTBREAK_DRILLS_VIEW, element: withSuspense(<OutbreakDrillsView />) },
  { path: PATHS.OUTBREAK_DRILLS_EDIT, element: withSuspense(<OutbreakDrillsEdit />) },
  { path: PATHS.OUTBREAK_LOGBOOK, element: withSuspense(<OutbreakLogbookList />) },
  { path: PATHS.OUTBREAK_LOGBOOK_CREATE, element: withSuspense(<OutbreakLogbookCreate />) },
  { path: PATHS.OUTBREAK_LOGBOOK_VIEW, element: withSuspense(<OutbreakLogbookView />) },
  { path: PATHS.OUTBREAK_LOGBOOK_EDIT, element: withSuspense(<OutbreakLogbookEdit />) },
  { path: PATHS.OUTBREAK_RUMOUR_REGISTER, element: withSuspense(<OutbreakRumourRegisterList />) },
  { path: PATHS.OUTBREAK_RUMOUR_REGISTER_CREATE, element: withSuspense(<OutbreakRumourRegisterCreate />) },
  { path: PATHS.OUTBREAK_RUMOUR_REGISTER_VIEW, element: withSuspense(<OutbreakRumourRegisterView />) },
  { path: PATHS.OUTBREAK_RUMOUR_REGISTER_EDIT, element: withSuspense(<OutbreakRumourRegisterEdit />) },
  { path: PATHS.OUTBREAK_EMERGENCY_PLANS, element: withSuspense(<EmergencyPlansList />) },
  { path: PATHS.OUTBREAK_EMERGENCY_PLANS_CREATE, element: withSuspense(<EmergencyPlansCreate />) },
  { path: PATHS.OUTBREAK_EMERGENCY_PLANS_VIEW, element: withSuspense(<EmergencyPlansView />) },
  { path: PATHS.OUTBREAK_EMERGENCY_PLANS_EDIT, element: withSuspense(<EmergencyPlansEdit />) },
{ path: PATHS.REPORTS_CENTER_LIST, element: withSuspense(<ReportsCenterList />) },
{ path: PATHS.REPORTS_CENTER_CREATE, element: withSuspense(<ReportsCenterCreate />) },
{ path: PATHS.REPORTS_DAILY_AUDIT, element: withSuspense(<ReportsDailyAudit />) },
      { path: PATHS.INSPECTIONS, element: withSuspense(<Inspections />) },
      { path: PATHS.QR, element: withSuspense(<QRScanner />) },
      { path: PATHS.REPORTS, element: withSuspense(<Reports />) },
      { path: PATHS.REPORTS_CONVEYANCE_INSPECTION, element: withSuspense(<ReportsConveyanceInspection />) },
      { path: PATHS.REPORTS_GENERAL_DECLARATIONS, element: withSuspense(<ReportsGeneralDeclarations />) },
      { path: PATHS.REPORTS_CARGO_SUMMARY, element: withSuspense(<ReportsCargoSummary />) },
      { path: PATHS.REPORTS_CARGO_CLEARANCE, element: withSuspense(<ReportsCargoClearance />) },
      { path: PATHS.REPORTS_CARGO_HAZARDOUS, element: withSuspense(<ReportsCargoHazardous />) },
      { path: PATHS.REPORTS_CARGO_CONDEMNATION, element: withSuspense(<ReportsCargoCondemnation />) },
      { path: PATHS.REPORTS_HUMAN_REMAINS_SUMMARY, element: withSuspense(<ReportsHumanRemainsSummary />) },
      { path: PATHS.REPORTS_TRAVELLER_SCREENING, element: withSuspense(<ReportsTravellerScreening />) },
      { path: PATHS.REPORTS_TRAVELLER_DISEASE_CONDITIONS, element: withSuspense(<ReportsTravellerDiseaseConditions />) },
      { path: PATHS.REPORTS_ENVIRONMENTAL_PREMISES, element: withSuspense(<ReportsEnvironmentalPremises />) },
      { path: PATHS.REPORTS_ENVIRONMENTAL_SANITARY_SCORECARD, element: withSuspense(<ReportsEnvironmentalSanitary />) },
      { path: PATHS.REPORTS_WATER_QUALITY, element: withSuspense(<ReportsWaterQuality />) },
      { path: PATHS.REPORTS_NOISE_QUALITY, element: withSuspense(<ReportsNoiseQuality />) },
      { path: PATHS.REPORTS_AIR_QUALITY, element: withSuspense(<ReportsAirQuality />) },
      { path: PATHS.REPORTS_VECTOR_SURVEILLANCE, element: withSuspense(<ReportsVectorSurveillance />) },
      { path: PATHS.REPORTS_VECTOR_USED_TYRES, element: withSuspense(<ReportsVectorUsedTyres />) },
      { path: PATHS.REPORTS_VECTOR_CONTROL, element: withSuspense(<ReportsVectorControl />) },
      { path: PATHS.REPORTS_WASTE_MANAGEMENT, element: withSuspense(<ReportsWasteManagement />) },
      { path: PATHS.REPORTS_AUDIT_TRAIL, element: withSuspense(<ReportsAuditTrail />) },
      { path: PATHS.REPORTS_CUSTOM, element: withSuspense(<ReportsCustomBuilder />) },
      { path: PATHS.REPORTS_CAPTURE_SUMMARY, element: withSuspense(<ReportsCaptureSummary />) },
      { path: PATHS.COMMUNICATIONS_MESSAGES, element: withSuspense(<CommunicationsMessagesPage />) },
      { path: PATHS.COMMUNICATIONS_THREAD, element: withSuspense(<CommunicationsMessagesPage />) },
      { path: PATHS.SETTINGS, element: withSuspense(<Settings />) },
      { path: PATHS.CALL_LOBBY, element: withSuspense(<CallLobby />) },
      { path: PATHS.SOP_REPOSITORY_LIST, element: withSuspense(<AdminSOPsRegulations />) },
      { path: PATHS.AUDIT_LOGS_LIST, element: withSuspense(<SystemAuditLogs />) },
      { path: PATHS.ADMIN_CARGO_ALERT_CONFIGURATION, element: withSuspense(<CargoAlertConfigurationPage />) },
      { path: PATHS.ADMIN_DOC_REQUIREMENTS_LIST, element: withSuspense(<DocumentRequirementsList />) },
{ path: PATHS.ADMIN_DOC_REQUIREMENTS_CREATE, element: withSuspense(<DocumentRequirementForm />) },
{ path: PATHS.ADMIN_DOC_REQUIREMENTS_VIEW, element: withSuspense(<DocumentRequirementForm />) },
{ path: PATHS.ADMIN_DOC_REQUIREMENTS_EDIT, element: withSuspense(<DocumentRequirementForm />) },
{ path: PATHS.ADMIN_COUNTRY_DOC_LIST, element: withSuspense(<CountryDocumentationList />) },
{ path: PATHS.ADMIN_COUNTRY_DOC_CREATE, element: withSuspense(<CountryDocumentationForm />) },
{ path: PATHS.ADMIN_COUNTRY_DOC_VIEW, element: withSuspense(<CountryDocumentationForm />) },
{ path: PATHS.ADMIN_COUNTRY_DOC_EDIT, element: withSuspense(<CountryDocumentationForm />) },
      { path: PATHS.ADMIN_USERS, element: withSuspense(<UserManagementLanding />) },
      { path: PATHS.ADMIN_ROLE_MATRIX, element: withSuspense(<RoleManagement />) },
      { path: PATHS.ADMIN_POE_CREATE, element: withSuspense(<CreatePoE />) },
      { path: PATHS.ADMIN_USER_PROFILE, element: withSuspense(<UserProfile />) },
      { path: PATHS.ADMIN_HAZARDOUS_BANNED_SUBSTANCES_LIST, element: withSuspense(<HazardousBannedSubstancesList />) },
      { path: PATHS.ADMIN_HAZARDOUS_BANNED_SUBSTANCES_CREATE, element: withSuspense(<HazardousBannedSubstancesForm />) },
    //   { path: PATHS.HELP, element: withSuspense(<Help />) },

    //human remains
      {path: PATHS.HR_CLEARANCE_LIST, element: withSuspense(<HumanRemainsList />)},
      {path: PATHS.HR_CLEARANCE, element: withSuspense(<HumanRemains />)},
      {path: PATHS.HR_CERTIFICATES, element: withSuspense(<HumanRemainsList />)},
      {path: PATHS.HR_CERTIFICATES_VIEW, element: withSuspense(<HumanRemainsCertificateView />)},

      {path: PATHS.CONVEYANCES_AIRCRAFT, element: withSuspense(<AircraftList />)},
      {path: PATHS.CONVEYANCES_AIRCRAFT_ADD, element: withSuspense(<AircraftListAdd />)},
      {path: PATHS.AIRCRAFT_CHECKLIST_VIEW, element: withSuspense(<AircraftChecklistView />)},
      {path: PATHS.AIRCRAFT_CHECKLIST_EDIT, element: withSuspense(<AircraftChecklistEdit />)},
   
{path: PATHS.GENERAL_DECLARATIONS_CREATE, element: withSuspense(<GeneralDeclarationCreate />)},
{path: PATHS.GENERAL_DECLARATIONS, element: withSuspense(<GeneralDeclarationList />)},
{path: PATHS.VEHICLE_GENERAL_DECLARATIONS_LIST, element: withSuspense(<VehicleGeneralDeclarationsList />)},
{path: PATHS.VEHICLE_GENERAL_DECLARATIONS_CREATE, element: withSuspense(<VehicleGeneralDeclarationForm />)},
{path: PATHS.VEHICLE_HAZARD_CHECKLISTS_LIST, element: withSuspense(<VehicleHazardChecklistList />)},
{path: PATHS.VEHICLE_HAZARD_CHECKLISTS_CREATE, element: withSuspense(<VehicleHazardChecklistCreate />)},
{path: PATHS.VEHICLE_HAZARD_CHECKLISTS_VIEW, element: withSuspense(<VehicleHazardChecklistView />)},
{path: PATHS.VEHICLE_HAZARD_CHECKLISTS_EDIT, element: withSuspense(<VehicleHazardChecklistEdit />)},

{path: PATHS.INSPECTIONS_PREMISES_CREATE, element: withSuspense(<PremiseCreate />)},
{path: PATHS.INSPECTIONS_PREMISES, element: withSuspense(<PremiseList />)},

{path: PATHS.INSPECTIONS_CARGO_CREATE, element: withSuspense(<CargoCreate />)},
{path: PATHS.INSPECTIONS_CARGO, element: withSuspense(<CargoList />)},

{path: PATHS.VECTOR_MONITORING, element: withSuspense(<VectorSurveillanceList />)},
{path: PATHS.VECTOR_MONITORING_CREATE, element: withSuspense(<VectorSurveillanceCreate />)},
{path: PATHS.VECTOR_MONITORING_VIEW, element: withSuspense(<VectorSurveillanceView />)},
{path: PATHS.VECTOR_MONITORING_EDIT, element: withSuspense(<VectorSurveillanceEdit />)},

{ path: PATHS.VECTOR_TYRES_LIST, element: withSuspense(<VectorTyreInspectionList />) },
{ path: PATHS.VECTOR_TYRES_CREATE, element: withSuspense(<VectorTyreInspectionForm />) },

{ path: PATHS.VECTOR_RODENTS_LIST, element: withSuspense(<RodentInspectionList />) },

// Ovitrap Monitoring
{ path: PATHS.VECTOR_OVITRAPS_LIST, element: withSuspense(<OvitrapsList />) },
{ path: PATHS.VECTOR_OVITRAPS_CREATE, element: withSuspense(<OvitrapsCreate />) },

// Residual Spraying
{ path: PATHS.VECTOR_RESIDUAL_SPRAY_LIST, element: withSuspense(<ResidualSprayList />) },
{ path: PATHS.VECTOR_RESIDUAL_SPRAY_CREATE, element: withSuspense(<ResidualSprayCreate />) },
{ path: PATHS.VECTOR_RESIDUAL_SPRAY_VIEW, element: withSuspense(<ResidualSprayView />) },
{ path: PATHS.VECTOR_RESIDUAL_SPRAY_EDIT, element: withSuspense(<ResidualSprayEdit />) },

// Yellow Fever Checks
{ path: PATHS.TRAVELLER_YF_LIST, element: withSuspense(<YellowFeverList />) },
{ path: PATHS.TRAVELLER_YF_CREATE, element: withSuspense(<YellowFeverCreate />) },

// Vector Reports
{ path: PATHS.VECTOR_TYRES_REPORT_MONTHLY, element: withSuspense(<UsedTyresMonthly />) },
{ path: PATHS.REPORTS_EXPORTS_TYRES, element: withSuspense(<UsedTyresMonthly />) },
{ path: PATHS.REPORTS_EXPORTS_GD, element: withSuspense(<ReportsExportsGeneralDeclaration />) },
{ path: PATHS.REPORTS_EXPORTS_SURVEILLANCE, element: withSuspense(<ReportsExportsSurveillance />) },
{ path: PATHS.REPORTS_EXPORTS_COC, element: withSuspense(<ReportsExportsChainOfCustody />) },
{ path: PATHS.REPORTS_EXPORTS_EXPIRY, element: withSuspense(<ReportsExportsLicenceExpiry />) },
{ path: PATHS.SMUGGLED_GOODS_LIST, element: withSuspense(<ReportsCargoSmuggledGoods />) },

{path: PATHS.INSPECTIONS_FOOD_WATER, element: withSuspense(<FoodWaterInspectionList />)},
{path: PATHS.INSPECTIONS_FOOD_WATER_CREATE, element: withSuspense(<FoodWaterInspectionCreate />)},

{path: PATHS.INSPECTIONS_SANITARY, element: withSuspense(<SanitaryInspectionList />)},
{path: PATHS.INSPECTIONS_SANITARY_CREATE, element: withSuspense(<SanitaryInspectionCreate />)},

{path: PATHS.WASTE_LOGS, element: withSuspense(<WasteDisposalList />)},
{path: PATHS.WASTE_LOGS_CREATE, element: withSuspense(<WasteDisposalCreate />)},

{path: PATHS.LICENCES_PREMISES, element: withSuspense(<PremiseLicenceList />)},
{path: PATHS.LICENCES_PREMISES_CREATE, element: withSuspense(<PremiseLicenceCreate />)},

{path: PATHS.FOOD_HANDLER_CERTS, element: withSuspense(<FoodHandlerList />)},
{path: PATHS.FOOD_HANDLER_CERTS_CREATE, element: withSuspense(<FoodHandlerCreate />)},

{path: PATHS.LICENCE_EXPIRY, element: withSuspense(<Watchlist />)},

{path: PATHS.THDF_CASES, element: withSuspense(<TravellerDeclarationView />)},
{path: PATHS.THDF_CASES_CREATE, element: withSuspense(<TravellerDeclarationCreate />)},

{ path: PATHS.DASHBOARDS_PORT_OPERATIONS, element: withSuspense(<DashboardPortOperations />) },
{ path: PATHS.DASHBOARDS_TRAVELLER_HEALTH, element: withSuspense(<DashboardTravellerHealth />) },
{ path: PATHS.DASHBOARDS_CONVEYANCE_OVERSIGHT, element: withSuspense(<DashboardConveyanceOversight />) },
{ path: PATHS.DASHBOARDS_CARGO_HOLDING_BAY, element: withSuspense(<DashboardCargoHoldingBay />) },
{ path: PATHS.DASHBOARDS_HUMAN_REMAINS, element: withSuspense(<DashboardHumanRemains />) },
{ path: PATHS.DASHBOARDS_VECTOR_SURVEILLANCE, element: withSuspense(<DashboardVectorSurveillance />) },
{ path: PATHS.DASHBOARDS_ENVIRONMENTAL_HEALTH, element: withSuspense(<DashboardEnvironmentalHealth />) },
{ path: PATHS.DASHBOARDS_OUTBREAKS_EPR, element: withSuspense(<DashboardOutbreaksEpr />) },

{ path: PATHS.DASHBOARDS_FINANCE_REVENUE, element: withSuspense(<DashboardFinanceRevenue />) },
{ path: PATHS.DASHBOARDS_FINANCE_ZIMRA, element: withSuspense(<DashboardFinanceZimra />) },
{ path: PATHS.DASHBOARDS_FINANCE_RECON, element: withSuspense(<DashboardFinanceRecon />) },

{ path: PATHS.DASHBOARDS_ADMIN_AUDIT, element: withSuspense(<DashboardAdmin />) },
{ path: PATHS.DASHBOARDS_ADMIN_MAKER_CHECKER, element: withSuspense(<DashboardAdminMakerChecker />) },
{ path: PATHS.DASHBOARDS_ADMIN_COMPLIANCE, element: withSuspense(<DashboardAdminCompliance />) },

{ path: PATHS.DASHBOARDS_OPS_PORT_DAILY, element: withSuspense(<DashboardOpsPortDaily />) },
{ path: PATHS.DASHBOARDS_OPS_COMPLIANCE, element: withSuspense(<DashboardOpsCompliance />) },
{ path: PATHS.DASHBOARDS_OPS_RISK, element: withSuspense(<DashboardOpsRisk />) },

{ path: PATHS.REPORTS_PORT_OPS, element: withSuspense(<PortOpsDashboard />) },
{ path: PATHS.REPORTS_VECTOR, element: withSuspense(<VectorDashboard />) },

    ],
  },
  {
    path: PATHS.NOT_FOUND,
    element: <ProtectedNotFound />,
  },
];

export default function AppRoutes() {
  const [showSplash, setShowSplash] = useState(true);
  const { officerId } = useAuthSession();
  const location = useLocation();
  const navigate = useNavigate();
  const routing = useRoutes(routes);

  useEffect(() => {
    if (typeof window === "undefined") {
      setShowSplash(false);
      return;
    }

    const timeout = window.setTimeout(() => setShowSplash(false), 2400);

    return () => window.clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (showSplash) return;
    if (officerId) return;
    if (location.pathname === PATHS.AUTH_LOGIN) return;

    navigate(PATHS.AUTH_LOGIN, { replace: true });
  }, [showSplash, officerId, location.pathname, navigate]);

  if (showSplash) {
    return <SplashScreen />;
  }

  return routing;
}
